import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Sparkles, Star, Clock, MapPin, Globe2, UserRound } from "lucide-react";
import TestimonialsWall from "../components/TestimonialsWall";
import WhatWeAre from "../components/WhatWeAre";
import { SERVICE_ICONS, DefaultIcon } from "../lib/icons";
import { useLang } from "../context/LangContext";
import Onboarding49Card from "../components/Onboarding49Card";
import VoiceBookingButton from "../components/VoiceBookingButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1692738611288-4d55c2078851?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxoZWxwaW5nJTIwaGFuZCUyMHJlYWNoaW5nJTIwc3Vuc2V0fGVufDB8fHx8MTc4NjY5OTUwNXww&ixlib=rb-4.1.0&q=85";
const REPAIR_IMG =
  "https://images.unsplash.com/photo-1679679811837-c28b2586f533?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxyZXBhaXIlMjB3b3JrZXIlMjBmcmllbmRseSUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NjY5OTUwNnww&ixlib=rb-4.1.0&q=85";
const SHOP_IMG =
  "https://images.unsplash.com/photo-1561715276-a2d087060f1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxjYXJyeWluZyUyMGdyb2NlcmllcyUyMHBhcGVyJTIwYmFnfGVufDB8fHx8MTc4NjY5OTQ5Nnww&ixlib=rb-4.1.0&q=85";
const BIZ_IMG =
  "https://images.unsplash.com/photo-1579389248774-07907f421a6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxmcmllbmRseSUyMHByb2Zlc3Npb25hbCUyMGhlbHBpbmclMjBwZXJzb258ZW58MHx8fHwxNzg2Njk5NDk2fDA&ixlib=rb-4.1.0&q=85";

const CORE_ASSISTS = ["railway", "airport", "hospital", "personal", "corporate", "stay", "goodfood", "findflat", "document", "shopping", "interview", "luggage", "babyold"];

function PopularAssists({ services }) {
  const list = CORE_ASSISTS.map((s) => services.find((x) => x.slug === s)).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="mt-10" data-testid="popular-assists">
      <div className="flex items-end justify-between mb-3">
        <h3 className="font-display text-2xl md:text-3xl font-bold" style={{ color: "#00604A" }}>Popular assists</h3>
        <span className="text-xs" style={{ color: "#667085" }}>Swipe →</span>
      </div>
      <div className="flex gap-3 md:gap-4 overflow-x-auto no-scrollbar pb-2 snap-x">
        {list.map((s) => (
          <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`popular-assist-${s.slug}`} className="group snap-start shrink-0 w-44 md:w-56">
            <div className="relative h-44 md:h-56 rounded-2xl overflow-hidden">
              <img src={s.photo} alt={s.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 p-3 text-white">
                <div className="font-bold text-sm md:text-base leading-tight">{s.name}</div>
                <div className="text-[11px] opacity-90">{s.price_inr ? `from ₹${s.price_inr}` : "Get a quote"}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const BENTO_SIZES = ["lg:col-span-6 md:col-span-6", "lg:col-span-3 md:col-span-3"];
function getBentoSize(idx) {
  if (idx % 5 === 0 || idx % 7 === 3) return BENTO_SIZES[0];
  return BENTO_SIZES[1];
}

export default function Landing() {
  const [services, setServices] = useState([]);
  const { t } = useLang();
  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data.services)).catch(() => {});
  }, []);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-14 md:pt-24 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <span className="label-eyebrow">{t("hero_eyebrow")}</span>
            <h1 className="mt-4 text-5xl sm:text-6xl md:text-7xl font-display font-bold leading-[0.95] tracking-tight" style={{ color: "var(--text-main)" }}>
              {t("hero_title_1")}
              <br />
              <span className="italic" style={{ color: "var(--secondary)" }}>{t("hero_title_2")}</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed max-w-xl" style={{ color: "var(--text-muted)" }}>
              {t("hero_sub")}
            </p>

            {/* On the Go strip */}
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 pill-strip">
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: "var(--secondary)" }} /> On the <b>GO</b></span>
              <span className="dot">•</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Anytime</span>
              <span className="dot">•</span>
              <span className="flex items-center gap-2"><Globe2 className="w-4 h-4" /> Anywhere</span>
              <span className="dot">•</span>
              <span className="flex items-center gap-2"><UserRound className="w-4 h-4" /> Minutes or Hours</span>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup-quick" data-testid="hero-quick-signup-btn">
                <button className="btn-primary inline-flex items-center gap-2">
                  Quick Signup — 30 sec <ArrowUpRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/services" data-testid="hero-book-btn">
                <button className="px-6 py-3 rounded-full border-2 border-[color:var(--primary)] text-[color:var(--primary)] bg-white text-sm font-bold hover:bg-[color:var(--primary)] hover:text-white transition-colors">
                  Book a Mate
                </button>
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="chip"><ShieldCheck className="w-3.5 h-3.5" /> ID-verified</span>
              <span className="chip"><Star className="w-3.5 h-3.5" /> Rated after every task</span>
              <span className="chip"><Clock className="w-3.5 h-3.5" /> Match under 10 min</span>
            </div>
          </div>

          {/* Hero collage */}
          <div className="lg:col-span-5 relative h-[420px] md:h-[520px] hidden lg:block">
            <img src={HERO_IMG} alt="Helping hands" className="absolute top-0 right-0 w-[78%] h-[62%] object-cover rounded-3xl border border-black/5 shadow-[0_20px_60px_-20px_rgba(29,46,95,0.35)]" />
            <img src={REPAIR_IMG} alt="Mate" className="absolute bottom-0 left-0 w-[62%] h-[55%] object-cover rounded-3xl border border-black/5 shadow-[0_20px_60px_-20px_rgba(29,46,95,0.35)]" />
            <div className="absolute top-[54%] left-[52%] bg-white border border-black/5 rounded-2xl px-4 py-3 shadow-xl">
              <div className="text-[10px] tracking-widest text-neutral-500 uppercase">Available Now</div>
              <div className="font-display text-lg leading-tight" style={{ color: "var(--primary)" }}>Ravi, Airport Mate</div>
              <div className="text-xs text-neutral-500 flex items-center gap-1"><Star className="w-3 h-3 fill-yellow-500 text-yellow-500" /> 4.9 · 312 tasks</div>
            </div>
          </div>
        </div>
      </section>

      {/* ₹49 Onboarding CTA — visible on every visit */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-3 pb-2" data-testid="onboarding-49-section">
        <VoiceBookingButton />
        <div className="mt-3">
          <Onboarding49Card />
        </div>
      </section>

      {/* TWO-WAY ENTRY — Help-Taker (collage) vs Help-Giver (mascot) */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-6 md:pt-4 pb-4" data-testid="two-way-entry">
        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          {/* I want HELP — real people collage */}
          <Link to="/signup-quick?role=customer" data-testid="entry-i-want-help"
                className="group relative rounded-3xl overflow-hidden border card-lift"
                style={{ borderColor: "#E4E7E5", minHeight: 260 }}>
            <div className="absolute inset-0 grid grid-cols-3 grid-rows-2 gap-0.5">
              {/* male */}
              <img src="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=500&auto=format&fit=crop&q=75" alt="" className="w-full h-full object-cover" loading="lazy" />
              {/* female */}
              <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=500&auto=format&fit=crop&q=75" alt="" className="w-full h-full object-cover" loading="lazy" />
              {/* elders */}
              <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=500&auto=format&fit=crop&q=75" alt="" className="w-full h-full object-cover" loading="lazy" />
              {/* family */}
              <img src="https://images.unsplash.com/photo-1609220136736-443140cffec6?w=500&auto=format&fit=crop&q=75" alt="" className="w-full h-full object-cover col-span-2" loading="lazy" />
              {/* group */}
              <img src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=500&auto=format&fit=crop&q=75" alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,96,74,0.05) 0%, rgba(0,96,74,0.85) 100%)" }} />
            <div className="relative p-6 md:p-8 flex flex-col justify-end h-full text-white" style={{ minHeight: 260 }}>
              <div className="text-xs uppercase tracking-widest opacity-90">For everyone</div>
              <h3 className="mt-1 font-display text-3xl md:text-4xl font-bold leading-tight">I want <span className="italic">help</span>.</h3>
              <p className="mt-2 text-sm md:text-base opacity-95 max-w-md">Elders, families, travellers, students — every kind of Indian gets a real Mate in under 10 minutes.</p>
              <span className="mt-4 inline-flex items-center gap-1.5 self-start rounded-full px-4 py-2 font-bold text-sm text-white shadow-lg"
                    style={{ background: "#F45118" }}>
                Quick Signup <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </Link>

          {/* I want to HELP — orange HeylpMATE mascot */}
          <Link to="/signup-quick?role=assistant" data-testid="entry-i-want-to-help"
                className="group relative rounded-3xl overflow-hidden border card-lift"
                style={{ borderColor: "#F4511833", minHeight: 260,
                         background: "linear-gradient(135deg,#FFF3EA 0%,#FFE8D5 60%,#FFDDC0 100%)" }}>
            <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_assist-on-demand/artifacts/q3ljyj80_file_00000000e2388211aef77ee9f26f9244.png"
                 alt="Orange HeylpMate mascot — I'll help"
                 className="absolute right-0 bottom-0 h-[95%] object-contain drop-shadow-xl select-none pointer-events-none"
                 loading="lazy" />
            <div className="relative p-6 md:p-8 flex flex-col justify-end h-full max-w-[65%]" style={{ minHeight: 260 }}>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "#F45118" }}>Become a Mate</div>
              <h3 className="mt-1 font-display text-3xl md:text-4xl font-bold leading-tight" style={{ color: "#00604A" }}>
                I'll <span className="italic" style={{ color: "#F45118" }}>help</span>.
              </h3>
              <p className="mt-2 text-sm md:text-base max-w-[240px]" style={{ color: "#4B5563" }}>
                Earn ₹150–₹500/hour. Zero platform fee for the first 5 tasks. Verified badge on approval.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 self-start rounded-full px-4 py-2 font-bold text-sm text-white shadow-lg"
                    style={{ background: "#00604A" }}>
                Quick Signup <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* USP band */}
      <section className="relative border-y border-black/5 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            ["14", "Assist categories"],
            ["10 min", "Average match time"],
            ["₹99", "Custom Assist starts"],
            ["₹199", "Featured Mate / mo"],
          ].map(([k, v]) => (
            <div key={k}>
              <div className="font-display text-3xl md:text-4xl" style={{ color: "var(--primary)" }}>{k}</div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-10">
          <div>
            <span className="label-eyebrow">what your Mate can help with</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-tight max-w-2xl" style={{ color: "#00604A" }}>
              Every kind of help. <span className="italic" style={{ color: "#F45118" }}>One tap away.</span>
            </h2>
          </div>
          <p className="max-w-md" style={{ color: "var(--text-muted)" }}>
            Real, verified humans — background-checked, trained, and rated. Pick a category, we'll match a Mate.
          </p>
        </div>

        {/* Category tile grid — 2 cols mobile / 3 cols desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5" data-testid="category-tiles">
          {[
            { slug: "travel",   name: "Travel Assist",        sub: "Airport · Bus · Cab",
              img: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=800&auto=format&fit=crop&q=75",
              emoji: "✈️", to: "/services?cat=travel" },
            { slug: "family",   name: "Family & Personal",    sub: "Elder care · Errands · Hospital",
              img: "https://images.unsplash.com/photo-1581579188871-45ea61f2a0c8?w=800&auto=format&fit=crop&q=75",
              emoji: "👨‍👩‍👧", to: "/services?cat=family" },
            { slug: "business", name: "Business & Career",    sub: "Office · Events · Career",
              img: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&auto=format&fit=crop&q=75",
              emoji: "💼", to: "/services?cat=business" },
            { slug: "skilled",  name: "Skilled & Legal",      sub: "Repairs · Legal · Design",
              img: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800&auto=format&fit=crop&q=75",
              emoji: "🛠️", to: "/services?cat=skilled" },
            { slug: "stay",     name: "Stay & Storage",       sub: "Flatmate · PG · Luggage",
              img: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=800&auto=format&fit=crop&q=75",
              emoji: "🏠", to: "/flatmate" },
            { slug: "unique",   name: "Unique Services",      sub: "NameCraft · Rail Luggage · Coolie",
              img: "https://images.unsplash.com/photo-1519689680058-324335c77eba?w=800&auto=format&fit=crop&q=75",
              emoji: "✨", to: "/services?cat=unique", featured: true },
          ].map((c) => (
            <Link
              key={c.slug}
              to={c.to}
              data-testid={`cat-tile-${c.slug}`}
              className="card-lift group relative overflow-hidden rounded-3xl border bg-white flex flex-col"
              style={{ borderColor: "#E4E7E5" }}
            >
              <div className="relative h-32 md:h-44 overflow-hidden">
                <img src={c.img} alt={c.name}
                     className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                     loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
                <div className="absolute top-3 left-3 w-9 h-9 rounded-full grid place-items-center text-lg shadow-lg"
                     style={{ background: "#00604A", color: "#fff" }}>
                  {c.emoji}
                </div>
                {c.featured && (
                  <div className="absolute top-3 right-3 rounded-full px-2 py-0.5 text-[10px] font-black text-white shadow-lg"
                       style={{ background: "#F45118" }}>
                    NEW
                  </div>
                )}
              </div>
              <div className="p-4 md:p-5 flex-1 flex flex-col">
                <h3 className="font-display text-base md:text-xl leading-tight" style={{ color: "#00604A" }}>{c.name}</h3>
                <p className="text-xs md:text-sm mt-1 line-clamp-1" style={{ color: "#667085" }}>{c.sub}</p>
                <span className="mt-auto pt-3 text-[11px] md:text-xs font-bold inline-flex items-center gap-1 group-hover:gap-2 transition-all"
                      style={{ color: "#F45118" }}>
                  Explore <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <PopularAssists services={services} />

        <div className="mt-6 text-center">
          <Link to="/services" data-testid="see-all-services"
                className="inline-flex items-center gap-1 text-sm font-bold underline underline-offset-4"
                style={{ color: "#00604A" }}>
            See all {services.length || 14} assists <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* CHILD AMBASSADOR — brand story strip */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-28" data-testid="child-ambassador">
        <div className="rounded-3xl overflow-hidden border grid md:grid-cols-2 items-center"
             style={{ borderColor: "#E4E7E5", background: "linear-gradient(135deg,#FFF8F1 0%,#EAF7F2 100%)" }}>
          <div className="p-8 md:p-12 flex flex-col justify-center order-2 md:order-1">
            <span className="chip mb-3" style={{ borderColor: "#F45118", color: "#F45118" }}>
              ✨ Our smallest Mate
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight" style={{ color: "#00604A" }}>
              Happy with<br /><span style={{ color: "#F45118" }}>HeylpMate</span>.
            </h2>
            <p className="mt-4 text-sm md:text-base max-w-lg" style={{ color: "#667085" }}>
              Meet our Child Ambassador — a reminder that helping starts young. Every HeylpMate booking funds
              real human warmth, not another delivery van.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/services" data-testid="ambassador-book"
                    className="rounded-full px-5 py-2.5 text-white font-bold text-sm shadow-md"
                    style={{ background: "#F45118" }}>
                Book a Mate
              </Link>
              <Link to="/register?role=assistant" data-testid="ambassador-become"
                    className="rounded-full px-5 py-2.5 font-bold text-sm border"
                    style={{ borderColor: "#00604A", color: "#00604A" }}>
                Become a Mate
              </Link>
            </div>
          </div>
          <div className="relative min-h-[320px] md:min-h-[460px] p-6 md:p-8 order-1 md:order-2">
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_assist-on-demand/artifacts/q3ljyj80_file_00000000e2388211aef77ee9f26f9244.png"
              alt="HeylpMate Child Ambassador — Happy with HeylpMate"
              className="absolute inset-0 w-full h-full object-contain p-4"
              loading="lazy"
              data-testid="ambassador-img"
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-24 md:pt-32">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="label-eyebrow">how heylpmate works</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-tight">
              Book. Match. Get it done.
            </h2>
            <p className="mt-5 leading-relaxed" style={{ color: "var(--text-muted)" }}>
              Customers pay a small platform fee — Mates receive the rest.
              Every rupee is transparent, every human is verified.
            </p>
            <div className="mt-8 rounded-3xl border border-black/10 bg-white p-6">
              <div className="label-eyebrow mb-3">example — ₹299 booking</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Customer pays</span><span className="font-semibold">₹299</span></div>
                <div className="flex justify-between text-neutral-500"><span>Platform commission</span><span>−₹99</span></div>
                <div className="border-t border-dashed border-black/10 my-2" />
                <div className="flex justify-between font-bold" style={{ color: "var(--primary)" }}><span>Mate receives</span><span>₹200</span></div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { t: "1. Choose an Assist", d: "Pick from 13 human-powered assist categories, right for your task." },
              { t: "2. Book a real Mate", d: "Every Mate is ID-verified. Match in under 10 minutes." },
              { t: "3. Get it done, live", d: "Track your Mate. Pay only when the assist is completed and rated." },
              { t: "4. Rate & repeat", d: "Rate honestly. Re-book your favourite Mate anytime." },
            ].map((x) => (
              <div key={x.t} className="rounded-3xl border border-black/10 bg-white p-6 card-lift">
                <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>{x.t}</div>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE ARE / ARE NOT */}
      <WhatWeAre />

      {/* TESTIMONIALS */}
      <TestimonialsWall />

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-24 md:pt-32">
        <div className="relative rounded-3xl overflow-hidden text-white p-10 md:p-16" style={{ background: "var(--primary)" }}>
          <div className="grain absolute inset-0 opacity-30" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <Sparkles className="w-6 h-6 mb-4" style={{ color: "var(--secondary)" }} />
              <h2 className="font-display text-4xl md:text-5xl leading-tight">
                Two ways to be part of <span style={{ color: "var(--secondary)" }}>HeylpMate</span>.
              </h2>
              <p className="mt-4 text-white/80 max-w-md leading-relaxed">
                Need help? Book a Mate — free, or Priority ₹99/mo. Want to help? Sign up as a Mate — free listing, or Featured ₹199/mo for top placement.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/register?role=customer" data-testid="cta-need-help">
                  <button className="px-6 py-3 rounded-full font-bold hover:opacity-90 transition-opacity" style={{ background: "var(--secondary)", color: "#fff" }}>
                    I Need Help
                  </button>
                </Link>
                <Link to="/register?role=assistant" data-testid="cta-become-mate">
                  <button className="px-6 py-3 rounded-full font-bold bg-white text-[color:var(--primary)] hover:opacity-90 transition-opacity">
                    I Want to Help
                  </button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block relative h-[280px]">
              <img src={BIZ_IMG} alt="Business support" className="absolute inset-0 w-full h-full object-cover rounded-2xl border border-white/20" />
            </div>
          </div>
        </div>
      </section>

      {/* SHOP BLOCK */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-24">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <img src={SHOP_IMG} alt="Shopping assist" className="rounded-3xl border border-black/10 object-cover w-full max-h-[520px]" />
          <div>
            <span className="label-eyebrow">stronger together</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight mt-3">
              Not a marketplace. <br /> A movement of <span className="italic" style={{ color: "var(--secondary)" }}>real Mates</span>.
            </h2>
            <p className="mt-5 leading-relaxed" style={{ color: "var(--text-muted)" }}>
              Behind every task is a verified human trained to help — from carrying your bags at a railway station
              to standing in a hospital queue with your parents. HeylpMate sends people, not promises.
            </p>
            <Link to="/become-mate" data-testid="cta-become-mate" className="inline-block mt-6">
              <button className="px-6 py-3 rounded-full border-2 border-[color:var(--primary)] text-[color:var(--primary)] bg-white font-bold hover:bg-[color:var(--primary)] hover:text-white transition-colors">
                Become a Mate →
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
